import { useState } from 'react';
import { useRef } from 'react';

import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { Document, pdfjs } from 'react-pdf';

import { RendererBaseProps } from '..';
import { PDFPage } from './PDFPage';
import { DEFAULT_PAGE_WIDTH } from '../constants';
import { DocViewerLoader } from './DocViewerLoader';
import { useGetDocPDFPresignedURL } from '@/api/queries/useGetDocPDFPresignedURL';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export const PDFDocumentRenderer = ({
  caseId,
  docId,
  highlights,
  getPageDimensions,
  visiblePages,
  getPagePosition,
  highlightedFactId,
  onHighlightClick,
  highlightedChunkId,
  totalHeight,
  zoom,
}: RendererBaseProps & { zoom: number }) => {
  const [documentReady, setDocumentReady] = useState(false);
  const [pdfWidth, setPdfWidth] = useState(DEFAULT_PAGE_WIDTH);

  const pdfCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  const { data: fileUrl } = useGetDocPDFPresignedURL(caseId, docId);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setDocumentReady(true);
    pdfCanvasRefs.current = Array(numPages).fill(null);
  };

  // We use the pdf here instead of the dimensions which are passed in just in case the doc doesn't have its dimensions in the DB
  const handlePageLoad = (page: { width: number; height: number }) => {
    if (!pdfWidth) setPdfWidth(page.width);
  };

  return (
    <div className="relative w-full" style={{ height: totalHeight }}>
      {fileUrl && (
        <Document
          key={fileUrl}
          loading={
            <div className="relative flex justify-center">
              <div className="fixed my-20 w-full">
                <DocViewerLoader />
              </div>
            </div>
          }
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {documentReady &&
            visiblePages.map((pageNumber) => {
              const pageDims = getPageDimensions(pageNumber);
              const pageWidth = pdfWidth * zoom;

              return (
                <PDFPage
                  key={`page-${pageNumber}`}
                  pageNumber={pageNumber}
                  top={getPagePosition(pageNumber)}
                  dimensions={{ height: pageDims.height, width: pageWidth }}
                  onLoadSuccess={handlePageLoad}
                  pdfCanvas={pdfCanvasRefs.current[pageNumber - 1]}
                  highlights={highlights}
                  highlightedFactId={highlightedFactId}
                  onHighlightClick={onHighlightClick}
                  highlightedChunkId={highlightedChunkId}
                />
              );
            })}
        </Document>
      )}
    </div>
  );
};
